const readPreference = (key, fallback) => {
  try {
    const raw = localStorage.getItem('user_preferences');
    if (!raw) return fallback;
    const prefs = JSON.parse(raw);
    if (key === 'format') return prefs.date_format || prefs.dateFormat || fallback;
    if (key === 'timezone') return prefs.timezone || fallback;
  } catch {
    return fallback;
  }
  return fallback;
};

export const formatDateByPreference = (value, format, timezone) => {
  if (!value) return '-';

  const resolvedFormat = format || readPreference('format', 'DD/MM/YYYY');
  const resolvedTimezone = timezone || readPreference('timezone', 'Asia/Kolkata');
  const date = new Date(value);
  const resolved = new Intl.DateTimeFormat('en-GB', {
    timeZone: resolvedTimezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);

  if (resolvedFormat === 'YYYY-MM-DD') return date.toISOString().slice(0, 10);
  if (resolvedFormat === 'MM/DD/YYYY') {
    const [day, month, year] = resolved.split('/');
    return `${month}/${day}/${year}`;
  }
  return resolved;
};

export const formatCurrencyByPreference = (value, currency = 'INR', locale = 'en-IN') => {
  const amount = Number(value || 0);
  const map = { INR: 'INR', USD: 'USD', AED: 'AED', EUR: 'EUR' };
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: map[currency] || 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
};

export const formatCompactRupees = (value) => {
  const amount = Number(value || 0);
  const prefix = amount < 0 ? '-' : '';
  const absolute = Math.abs(amount);

  if (absolute >= 10000000) {
    return `${prefix}₹${(absolute / 10000000).toFixed(2).replace(/\.?0+$/, '')}Cr`;
  }

  if (absolute >= 100000) {
    return `${prefix}₹${(absolute / 100000).toFixed(2).replace(/\.?0+$/, '')}L`;
  }

  if (absolute >= 1000) {
    return `${prefix}₹${(absolute / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  }

  return `${prefix}₹${absolute.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

export const formatDateOnly = (value) => {
  if (!value) return '-';
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '-';
  }
};

export const formatTimestamp = (value) => {
  if (!value) return 'Never Logged In';
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return 'Never Logged In';
    return d.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch {
    return 'Never Logged In';
  }
};

export const autoFormatDate = (inputVal, prevVal = '', format = 'DD/MM/YYYY') => {
  if (!inputVal) return '';

  const isDeleting = Boolean(prevVal && inputVal.length < prevVal.length);

  if (format === 'YYYY-MM-DD') {
    const digits = inputVal.replace(/\D/g, '').slice(0, 8);
    if (isDeleting) {
      if (digits.length <= 4) return digits;
      if (digits.length <= 6) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
      return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6)}`;
    }
    if (digits.length < 4) return digits;
    if (digits.length === 4) return `${digits}-`;
    if (digits.length < 6) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
    if (digits.length === 6) return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-`;
    return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6)}`;
  }

  // Handle manual slash after 1 digit (e.g. "7/" -> "07/")
  if (!isDeleting && inputVal.endsWith('/')) {
    if (/^\d\/$/.test(inputVal)) {
      return `0${inputVal[0]}/`;
    }
    if (/^\d{2}\/\d\/$/.test(inputVal)) {
      const parts = inputVal.split('/');
      return `${parts[0]}/0${parts[1]}/`;
    }
  }

  const digits = inputVal.replace(/\D/g, '').slice(0, 8);

  if (isDeleting) {
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  }

  if (digits.length < 2) return digits;
  if (digits.length === 2) return `${digits}/`;
  if (digits.length < 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  if (digits.length === 4) return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};
